import posthog from 'posthog-js';
import { listenForCookieYesConsentUpdates } from './cookieyes';
import type { CookieYesConsentState } from './cookieyes';
import type { PostHogConfig } from 'posthog-js';

type Config = {
	key: string;
} & Partial<PostHogConfig>;

export function createClient(config: Config) {
	const { key, ...settings } = config;
	const ph = posthog.init(config.key, {
		api_host: config.api_host ?? 'https://app.posthog.com',
		autocapture: {
			dom_event_allowlist: ['submit'],
		},
		ui_host: 'https://us.posthog.com',
		persistence: 'memory',
		person_profiles: 'identified_only',
		disable_surveys: true,
		disable_session_recording: true,
		capture_pageview: 'history_change',
		capture_dead_clicks: false,
		secure_cookie: true,
		// Prevent extra feature flag requests after the browser window is opened
		// This makes the client fully rely on the server-side calculated flags
		advanced_disable_feature_flags_on_first_load: true,
		...settings,
	})!;

	const updateConsentPersonProperties = (state: CookieYesConsentState) => {
		ph.setPersonProperties({
			cookieConsentId: state.consentID,
			cookieConsentActionComplete: state.isUserActionCompleted,
		});
	};

	listenForCookieYesConsentUpdates({
		onRead: updateConsentPersonProperties,
		onBannerLoad: updateConsentPersonProperties,
		onConsentUpdate: ({ accepted, rejected, state }) => {
			// CookieYes can emit bootstrap/default updates; only capture an action after completion is confirmed.
			if (!state.isUserActionCompleted) {
				ph.setPersonProperties({
					cookieConsentId: state.consentID,
					cookieConsentActionComplete: false,
				});
				return;
			}

			ph.capture('cookieConsent', {
				cookiesAccepted: accepted,
				cookiesRejected: rejected,
				cookieConsentId: state.consentID,
				cookieLaw: state.activeLaw,
			});
			ph.setPersonProperties({ cookieConsentActionComplete: true });
		},
	});

	return ph;
}
